"use client";

import { useCallback, useRef, useState } from "react";

import {
	type FieldValue,
	runValidators,
	type Validator,
} from "../utils/validation";

export type ValidationRules = Record<string, Validator[]>;

/**
 * Gestion centralisée des erreurs de formulaire.
 *
 * - `validateField(name, value)` : à appeler dans onChange / onBlur —
 *   l'erreur est affichée en temps réel sous le champ.
 * - `validateAll(values, names?)` : à appeler à la soumission — force
 *   l'affichage de toutes les erreurs et retourne `true` si tout est valide.
 * - `clearErrors()` : réinitialise erreurs et état "touché".
 */
export function useFormValidation(rules: ValidationRules) {
	const [errors, setErrors] = useState<Record<string, string>>({});
	const touchedRef = useRef<Record<string, boolean>>({});

	const setFieldError = useCallback((name: string, error: string | null) => {
		setErrors((prev) => {
			if (error) {
				if (prev[name] === error) return prev;
				return { ...prev, [name]: error };
			}
			if (!(name in prev)) return prev;
			const next = { ...prev };
			delete next[name];
			return next;
		});
	}, []);

	const validateField = useCallback(
		(name: string, value: FieldValue) => {
			touchedRef.current[name] = true;
			const error = runValidators(value, rules[name]);
			setFieldError(name, error);
			return error;
		},
		[rules, setFieldError],
	);

	const validateAll = useCallback(
		(values: Record<string, FieldValue>, names?: string[]) => {
			const keys = names ?? Object.keys(rules);
			const next: Record<string, string> = {};
			let valid = true;

			for (const key of keys) {
				touchedRef.current[key] = true;
				const error = runValidators(values[key], rules[key]);
				if (error) {
					next[key] = error;
					valid = false;
				}
			}

			setErrors(next);
			return valid;
		},
		[rules],
	);

	const clearErrors = useCallback(() => {
		setErrors({});
		touchedRef.current = {};
	}, []);

	return { errors, validateField, validateAll, clearErrors, setFieldError };
}
